import * as React from 'react';
import { Query } from 'react-apollo';

import AccountSettingsCourses from 'bundles/account-settings/queries/AccountSettingsCoursesQuery.graphql';
import AccountSettingsPartners from 'bundles/account-settings/queries/AccountSettingsPartnersQuery.graphql';
import type {
  AccountSettingsCoursesQuery,
  AccountSettingsCoursesQueryVariables,
} from 'bundles/account-settings/queries/__generated__/AccountSettingsCoursesQuery';
import type {
  AccountSettingsPartnersQuery,
  AccountSettingsPartnersQueryVariables,
} from 'bundles/account-settings/queries/__generated__/AccountSettingsPartnersQuery';
import type { UnsubscribeStatus } from 'bundles/account-settings/types/UnsubscribeStatus';

import _t from 'i18n!nls/account-settings';

type Props = {
  status: UnsubscribeStatus;
  children: (args: {
    header: string;
    subheader: string;
    cta: { primary: string; secondary: string; secondaryButtonType?: 'link' };
  }) => React.ReactElement;
};

export const UnsubscribeCopyProvider: React.FC<Props> = ({ status, children }) => {
  const { preference: unsubscribePreference } = status ?? {};

  const [namespace = '', id = '', preference = ''] = unsubscribePreference?.split('.') ?? [];

  let header = _t('Unsubscribe from Coursera emails');

  let subheader = _t(
    `To update your email preferences so that you receive fewer emails but don’t miss out on any free 
    job resources and paid surveys, go to you communication preferences page under Settings for more.`
  );

  const primaryCTA = _t('Unsubscribe');

  let secondaryCTA = _t('Get fewer emails');
  let secondaryButtonType: 'link' | undefined;

  if (namespace === 'general') {
    switch (preference) {
      case 'recommendations':
        subheader = _t('You are unsubscribing from weekly personalized course recommendations.');
        break;
      case 'promotions':
        subheader = _t('You are unsubscribing from promotions, new courses and programs, and special events.');
        break;
      case 'volunteer':
        subheader = _t(
          'You are unsubscribing from surveys and invitations to help us improve Coursera content and your experience.'
        );
        break;
    }
  }

  if (unsubscribePreference === 'degree.coursera.eoi') {
    subheader = _t('You are unsubscribing from information about online Master’s and Bachelor’s Degree programs.');
  }

  if (namespace === 'partner' && preference === 'notifications') {
    return (
      <Query<AccountSettingsPartnersQuery, AccountSettingsPartnersQueryVariables>
        variables={{ queryByIdId: id }}
        query={AccountSettingsPartners}
        context={{
          clientName: 'gatewayGql',
        }}
      >
        {({ data }) => {
          const partnerName = data?.Partner?.queryById?.name;

          if (!partnerName) {
            return null;
          }

          subheader = _t(
            'You are unsubscribing from general information about online and on-campus courses and programs, special events, and publications from #{partnerName}. These can include job resources, paid surveys, and additional career information.',
            { partnerName }
          );

          return children({
            header,
            subheader,
            cta: {
              primary: primaryCTA,
              secondary: secondaryCTA,
            },
          });
        }}
      </Query>
    );
  }

  if (namespace === 'opencourse') {
    return (
      <Query<AccountSettingsCoursesQuery, AccountSettingsCoursesQueryVariables>
        variables={{ queryByIdId: id }}
        query={AccountSettingsCourses}
        context={{
          clientName: 'gatewayGql',
        }}
      >
        {({ data }) => {
          const courseName = data?.Course?.queryById?.name;

          if (!courseName) {
            return null;
          }

          switch (preference) {
            case 'reminders':
              subheader = _t(
                'Reminder emails are related to your course progress, such as deadline reminders. You are unsubscribing from these emails for #{courseName}',
                { courseName }
              );
              break;
            case 'announcements':
              subheader = _t(
                'Announcements are sent from your course instructor, and may include special events, new materials, and learning or job resources exclusively offered to those enrolled in the course. You are unsubscribing from these emails for #{courseName}',
                { courseName }
              );
              break;
            case 'discourse': // Course forums
              subheader = _t(
                'Forums emails notify you of replies on one of your threads, likes/dislikes or any other communication directed towards you.  You are unsubscribing from these emails for #{courseName}',
                { courseName }
              );
              break;
          }

          return children({
            header,
            subheader,
            cta: {
              primary: primaryCTA,
              secondary: secondaryCTA,
            },
          });
        }}
      </Query>
    );
  }

  if (unsubscribePreference === 'enterpriseLearner.coursera.contentAssignments') {
    header = _t('Unsubscribe from assigned content emails');
    subheader = _t('Please confirm your decision to unsubscribe from all future assigned content emails');
    secondaryCTA = _t('View email settings');
    secondaryButtonType = 'link';
  }
  if (unsubscribePreference === 'enterpriseAdmin.coursera.managerInsightReport') {
    header = _t('We are sorry to see you go');
    subheader = _t("Please confirm your decision to unsubscribe from your team's monthly learning insights");
    secondaryCTA = '';
    secondaryButtonType = 'link';
  }

  return children({
    header,
    subheader,
    cta: {
      primary: primaryCTA,
      secondary: secondaryCTA,
      secondaryButtonType,
    },
  });
};
